import React, { useState, useMemo } from 'react';
import { Typography, Input, List, Avatar, Space, Card, Empty } from 'antd';
import { SearchOutlined, EnvironmentOutlined } from '@ant-design/icons';

// Forms
import EnhancedTripleseat from './forms/EnhancedTripleseat';
import EnhancedHoneybook from './forms/EnhancedHoneybook';
import EnhancedConversate from './forms/EnhancedConversate';
import EnhancedPlanningpod from './forms/EnhancedPlanningpod';
import EnhancedEventtemple from './forms/EnhancedEventtemple';
import Enhanced17Hats from './forms/Enhanced17Hats';
import EnhancedBoothbook from './forms/EnhancedBoothbook';
import EnhancedCheckCherry from './forms/EnhancedCheckCherry';
import EnhancedDJEventPlanner from './forms/EnhancedDJEventPlanner';
import EnhancedReleventful from './forms/EnhancedReleventful';
import EnhancedHubspot from './forms/EnhancedHubspot';
import EnhancedPerfectVenue from './forms/EnhancedPerfectVenue';
import EnhancedMailChimp from './forms/EnhancedMailChimp';
import EnhancedEventIntelligence from './forms/EnhancedEventIntelligence';
import EnhancedCaterease from './forms/EnhancedCaterease';
import EnhancedSMPLSystem from './forms/EnhancedSMPLSystem';
import EnhancedGoHighLevel from './forms/EnhancedGoHighLevel';
import EnhancedNurturePro from './forms/EnhancedNurturePro';
import CommonCard from '../../global/card';
import { useGenericQuery } from '../../../queries/use-generic-query';
import SOURCES from '../../../utils/constants';
import { cn } from '../../../utils/cn';
import { QUERY_KEYS } from '../../../lib/query-constants';
import CommonInput from '../../global/input';
import { useLocation, useHistory } from 'react-router-dom';

const { Text, Title } = Typography;

const DESTINATIONS_LIST = [
    { id: "1", title: "17Hats", icon: "images/17hats.png", company: SOURCES.DESTINATION_17HATS },
    { id: "7", title: "BoothBook", icon: "images/boothbook.png", company: SOURCES.DESTINATION_BOOTHBOOK },
    { id: "13", title: "Caterease", icon: "images/caterease.png", company: SOURCES.DESTINATION_CATEREASE },
    { id: "8", title: "Check Cherry", icon: "images/checkcherry.png", company: SOURCES.DESTINATION_CHECK_CHERRY },
    { id: "3", title: "Conversate", icon: "images/conversate.png", company: SOURCES.DESTINATION_CONVERSATE },
    { id: "9", title: "DJ Event Planner", icon: "images/djevent.jpeg", company: SOURCES.DESTINATION_DJ_EVENT_PLANNER },
    { id: "20", title: "Event Intelligence", icon: "images/eventIntelligence.png", company: SOURCES.DESTINATION_EVENT_INTELLIGENCE },
    { id: "6", title: "EventTemple", icon: "images/eventtemple.png", company: SOURCES.DESTINATION_EVENT_TEMPLE },
    { id: "5", title: "HoneyBook", icon: "images/honeybook.png", company: SOURCES.DESTINATION_HONEYBOOK },
    { id: "11", title: "Hubspot", icon: "images/hubspot.png", company: SOURCES.DESTINATION_HUBSPOT },
    { id: "19", title: "MailChimp", icon: "images/mailchimp.png", company: SOURCES.DESTINATION_MAILCHIMP },
    { id: "12", title: "Perfect Venue", icon: "images/perfectvenue.png", company: SOURCES.DESTINATION_PERFECT_VENUE },
    { id: "4", title: "PlanningPod", icon: "images/planningpod.png", company: SOURCES.DESTINATION_PLANNINGPOD },
    { id: "10", title: "Releventful", icon: "images/releventful.png", company: SOURCES.DESTINATION_RELEVENTFUL },
    { id: "21", title: "SMPL System", icon: "images/SMPLsystem.png", company: SOURCES.DESTINATION_SMPLSYSTEM },
    { id: "2", title: "Tripleseat", icon: "images/tripleseat.png", company: SOURCES.DESTINATION_TRIPPLE_SEAT },
    { id: "14", title: "GoHighLevel", icon: "images/gohighlevel.png", company: SOURCES.DESTINATION_GOHIGHLEVEL, enterprise: true },
    { id: "15", title: "Bloom", icon: "images/bloom.png", company: SOURCES.DESTINATION_BLOOM, enterprise: true },
    { id: "16", title: "Profitflo", icon: "images/profitflo.png", company: SOURCES.DESTINATION_PROFITFLO, enterprise: true },
    { id: "17", title: "Venue", icon: "images/venue.png", company: SOURCES.DESTINATION_VENUE, enterprise: true },
    { id: "18", title: "NurturePro", icon: "images/NurturePro.png", company: SOURCES.DESTINATION_NURTUREPRO, enterprise: true },
    { id: "22", title: "Venue Lead Pro", icon: "images/VenueLead.png", company: SOURCES.DESTINATION_VENUELEADPRO, enterprise: true },
];

const EnhancedDestinations = () => {
    const location = useLocation();
    const history = useHistory();
    const activeTab = new URLSearchParams(location.search).get("tab");
    const [searchTerm, setSearchTerm] = useState("");
    const [activeDestination, setActiveDestination] = useState(DESTINATIONS_LIST.find(dest => dest.id === activeTab) || DESTINATIONS_LIST[0]);

    // Data Fetching
    const { data: destinationsData, isLoading: destLoading } = useGenericQuery({
        endpoint: "integration",
        queryKey: QUERY_KEYS.destinations.lists(),
    });

    const { data: zapierSetup, isLoading: zapierLoading } = useGenericQuery({
        endpoint: "zapier",
        queryKey: QUERY_KEYS.zapier.lists(),
    });

    const { data: payments, isLoading: paymentsLoading } = useGenericQuery({
        endpoint: "payment/get-paymentHistory",
        queryKey: ["paymentHistory"],
    });

    const isEnterprisePlan = payments?.planName === SOURCES.USER_PLAN_ENTERPRISE;

    const filteredDestinations = useMemo(() => {
        // 1. Sort always
        const sorted = [...DESTINATIONS_LIST].sort((a, b) =>
            a.title.localeCompare(b.title)
        );

        const normalizedSearch = searchTerm
            ?.toLowerCase()
            .replace(/\s+/g, '');

        // 2. If searching → search ALL destinations
        if (searchTerm && normalizedSearch) {
            return sorted.filter(dest => {
                const titleMatch = dest.title
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .includes(normalizedSearch);

                return titleMatch;
            });
        }

        // 3. No search → apply enterprise visibility
        return sorted
    }, [searchTerm, isEnterprisePlan]);



    const isLoading = destLoading || zapierLoading || paymentsLoading;

    // Component mapping for each destination
    const renderForm = () => {
        const commonProps = {
            destinations: destinationsData,
            zapierSetup: zapierSetup,
            activeDestination: activeDestination
        };

        const btnClassName = "!bg-primary text-white h-10 min-w-[130px] !border-none hover:!border-none "
        switch (activeDestination.company) {
            case SOURCES.DESTINATION_TRIPPLE_SEAT:
                return <EnhancedTripleseat {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_HONEYBOOK:
                return <EnhancedHoneybook {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_CONVERSATE:
                return <EnhancedConversate {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_PLANNINGPOD:
                return <EnhancedPlanningpod {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_EVENT_TEMPLE:
                return <EnhancedEventtemple {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_17HATS:
                return <Enhanced17Hats {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_BOOTHBOOK:
                return <EnhancedBoothbook {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_CHECK_CHERRY:
                return <EnhancedCheckCherry {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_DJ_EVENT_PLANNER:
                return <EnhancedDJEventPlanner {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_RELEVENTFUL:
                return <EnhancedReleventful {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_HUBSPOT:
                return <EnhancedHubspot {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_PERFECT_VENUE:
                return <EnhancedPerfectVenue {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_MAILCHIMP:
                return <EnhancedMailChimp {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_EVENT_INTELLIGENCE:
                return <EnhancedEventIntelligence {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_CATEREASE:
                return <EnhancedCaterease {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_SMPLSYSTEM:
                return <EnhancedSMPLSystem {...commonProps} btnClassName={btnClassName} />;
            // case SOURCES.DESTINATION_GOHIGHLEVEL:
            //     return <EnhancedGoHighLevel {...commonProps} btnClassName={btnClassName} />;
            // case SOURCES.DESTINATION_BLOOM:
            //     return <EnhancedBloom {...commonProps} btnClassName={btnClassName} />;
            // case SOURCES.DESTINATION_PROFITFLO:
            //     return <EnhancedProfitflo {...commonProps} btnClassName={btnClassName} />;
            // case SOURCES.DESTINATION_VENUE:
            //     return <EnhancedVenue {...commonProps} btnClassName={btnClassName} />;
            case SOURCES.DESTINATION_GOHIGHLEVEL:
            case SOURCES.DESTINATION_NURTUREPRO:
            case SOURCES.DESTINATION_VENUELEADPRO:
            case SOURCES.DESTINATION_VENUE:
            case SOURCES.DESTINATION_BLOOM:
            case SOURCES.DESTINATION_PROFITFLO:
                return <EnhancedNurturePro {...commonProps} btnClassName={btnClassName} />;
            // case SOURCES.DESTINATION_VENUELEADPRO:
            //     return <EnhancedVenueLeadPro {...commonProps} btnClassName={btnClassName} />;
            default:
                return (
                    <div className="flex flex-col items-center justify-center h-full p-12 text-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                        <Avatar size={64} src={activeDestination.icon} className="mb-4 shadow-sm" />
                        <Title level={4}>{activeDestination.title}</Title>
                        <Text type="secondary" className="max-w-md">
                            Configuration for {activeDestination.title} is coming soon in the enhanced UI.
                            Please use the legacy interface if urgent.
                        </Text>
                    </div>
                );
        }
    };

    return (
        <CommonCard
            isLoading={{
                show: isLoading,
                loaderCmp: (
                    <div className="flex flex-col items-center justify-center p-20 !min-h-[600px]">
                        <div className="relative size-12 mb-4">
                            <div className="absolute inset-0 rounded-full border-4 border-primary/10" />
                            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin" />
                        </div>
                        <Text type="secondary" className="animate-pulse">Loading destinations...</Text>
                    </div>
                )
            }}
            className={cn("!p-0 overflow-hidden", isLoading && "min-h-[600px]")}
        >
            <div className={cn("flex flex-col lg:flex-row h-auto lg:h-[calc(100vh-200px)] min-h-[600px]", isLoading && "invisible")}>
                {/* Sidebar */}
                <div className="w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-gray-100 flex flex-col items-center bg-white px-2 h-[300px] lg:h-auto">
                    {/* <div className="p-0 border-b border-gray-100"> */}
                    <div className="flex items-center self-start gap-2 mb-2 mt-2 lg:mt-0">
                        <EnvironmentOutlined className="text-primary text-xl" />
                        <Title level={5} className="!mb-0">Destinations</Title>
                    </div>
                    <div className="sticky top-0 left-0 z-10 bg-white w-full">
                        <CommonInput
                            placeholder={"Search destinations..."}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            prefix={<SearchOutlined className="text-gray-400" />}
                        />
                    </div>
                    {/* </div> */}

                    <div className="flex-1 py-2 sidebar-scroll overflow-y-auto self-start w-full px-2 max-h-[800px]">
                        {filteredDestinations.length > 0 ? (
                            <List
                                dataSource={filteredDestinations}
                                renderItem={(item) => (
                                    <div
                                        onClick={() => {
                                            history.push(`?tab=${item.id}`);
                                            setActiveDestination(item)
                                        }}
                                        className={cn(
                                            "!flex items-center relative px-3 py-2 mb-1 text-gray-900 rounded-lg hover:bg-gray-100 hover:text-primary text-base font-medium gap-2 cursor-pointer",
                                            activeDestination.id === item.id
                                            && "bg-gray-100"
                                        )}
                                    >
                                        <div
                                            className={`absolute top-1/2 -translate-y-1/2 right-2 w-1 h-5 bg-[#2dbdf2] rounded-md ${activeDestination.id === item.id ? "" : "hidden"
                                                }`}
                                        />
                                        <div className={cn(
                                            "size-10 rounded-lg flex items-center justify-center p-1.5 transition-all shadow-sm bg-white",
                                            activeDestination.id === item.id ? "scale-105 shadow-md ring-2 ring-primary/20" : "group-hover:scale-105"
                                        )}>
                                            <img src={item.icon} alt={item.title} className="w-full h-full object-contain" />
                                        </div>
                                        <div className="flex flex-col flex-1 overflow-hidden">
                                            <Text strong className={cn(
                                                "text-sm truncate",
                                                activeDestination.id === item.id ? "text-primary" : "text-gray-700"
                                            )}>
                                                {item.title}
                                            </Text>
                                            {item.enterprise && (
                                                <Text className="text-[10px] text-orange-500 font-bold uppercase tracking-wider">Enterprise</Text>
                                            )}
                                        </div>
                                    </div>
                                )}
                            />
                        ) : (
                            <Empty description="No destinations found" className="mt-12" />
                        )}
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto bg-gray-50/30 p-4 lg:p-8 content-scroll h-[calc(100vh-500px)] lg:h-auto">
                    <div className="max-w-4xl mx-auto h-full">
                        {renderForm()}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .sidebar-scroll::-webkit-scrollbar,
                .content-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                .sidebar-scroll::-webkit-scrollbar-thumb,
                .content-scroll::-webkit-scrollbar-thumb {
                    background: #e5e7eb;
                    border-radius: 10px;
                }
                .sidebar-scroll::-webkit-scrollbar-track,
                .content-scroll::-webkit-scrollbar-track {
                    background: transparent;
                }
            `}</style>
        </CommonCard>
    );
};

export default EnhancedDestinations;